/**
 * HntPaymentPane — Paket B (premium-checkout-redesign)
 *
 * HNT-Tab von /checkout. Quote via requestCryptoQuote (StrictMode-sicheres
 * Promise-Caching wie subscription/CryptoCheckout.tsx), QR ≥ 280px,
 * Countdown aus expires_at, 8s-Polling bis Premium aktiv ist.
 *
 * Statusleiste bewusst 2-stufig (Handover: BE meldet keine Zwischenstufe
 * "Zahlung erkannt") — is-wait bis das Polling ein aktives Abo mit diesem
 * Plan sieht, danach is-done kurz vor der Weiterleitung.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { Check, Loader2 } from 'lucide-react'
import type { Plan } from '../subscription/useSubscriptionPlans'
import type { DurationMonths } from '../subscription/subscriptionUtils'
import { buildHeliumPaymentUrl, type CryptoQuote, type Subscription } from '../subscription/useSubscription'
import { activeBcp47 } from '../../lib/locale'
import { formatMoney } from '../premium/planMath'
import { resolveSpecPlanId } from '../premium/legacyPlanMap'

const POLL_INTERVAL_MS = 8000

type HntPaymentPaneProps = {
  plan: Plan
  duration: DurationMonths
  requestCryptoQuote: (planId: string, durationMonths: number) => Promise<CryptoQuote>
  refetchSubscription: () => Promise<{ data: Subscription | null | undefined }>
  onSuccess: () => void
}

// Format wie CryptoCheckout.formatCountdown (Tage/Stunden/Minuten), aber
// unter 1h mm:ss statt "Nmin" — hier läuft die Uhr sekundengenau mit.
function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00'
  const totalMin = Math.floor(ms / 60000)
  const days = Math.floor(totalMin / 1440)
  const hours = Math.floor((totalMin % 1440) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${totalMin % 60}min`
  const totalSec = Math.floor(ms / 1000)
  const m = String(Math.floor(totalSec / 60)).padStart(2, '0')
  const s = String(totalSec % 60).padStart(2, '0')
  return `${m}:${s}`
}

export function HntPaymentPane({ plan, duration, requestCryptoQuote, refetchSubscription, onSuccess }: HntPaymentPaneProps) {
  const { t } = useTranslation('checkout')
  const [quote, setQuote] = useState<CryptoQuote | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<'address' | 'amount' | null>(null)
  const [now, setNow] = useState<number>(() => Date.now())
  const [done, setDone] = useState(false)
  // Promise cachen: unter React-StrictMode (Doppel-Mount im Dev) wird die
  // Quote so genau einmal angefordert (siehe CryptoCheckout.tsx-Kommentar).
  const quotePromiseRef = useRef<Promise<CryptoQuote> | null>(null)
  const [requestNonce, setRequestNonce] = useState(0)

  useEffect(() => {
    let active = true
    if (!quotePromiseRef.current) {
      quotePromiseRef.current = requestCryptoQuote(plan.id, duration)
    }
    quotePromiseRef.current
      .then((q) => { if (active) { setQuote(q); setError(null) } })
      .catch(() => { if (active) setError(t('hnt.quoteError')) })
    return () => { active = false }
    // requestNonce (Deps) triggert eine gezielte Neuanforderung nach Ablauf.
  }, [plan.id, duration, requestCryptoQuote, t, requestNonce])

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!quote || done) return
    let active = true
    const poll = async () => {
      const res = await refetchSubscription()
      const sub = res && 'data' in res ? res.data : undefined
      // PremiumPayment::PaymentProcessingService#apply_upgrade_code schreibt fuer
      // "professional"-Krypto-Zahlungen intern payment.plan = "professional_new"
      // (payment_processing_service.rb) — ein direkter sub.plan_id === plan.id
      // haengt fuer diesen Tarif nie an, weil das BE nach der Zahlung nie exakt
      // die angeforderte ID zurueckgibt. resolveSpecPlanId normalisiert beide
      // Seiten auf denselben SPEC-Tarif, bevor sie verglichen werden.
      const subSpecPlanId = sub ? resolveSpecPlanId(sub.plan_id) : null
      if (active && sub && sub.status === 'active' && subSpecPlanId === resolveSpecPlanId(plan.id)) {
        setDone(true)
        setTimeout(onSuccess, 500)
      }
    }
    const id = setInterval(() => { void poll() }, POLL_INTERVAL_MS)
    return () => { active = false; clearInterval(id) }
  }, [quote, done, refetchSubscription, onSuccess, plan.id])

  const copy = useCallback(async (text: string, which: 'address' | 'amount') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(which)
      setTimeout(() => setCopied(null), 1400)
    } catch {
      /* Clipboard nicht verfügbar — kein harter Fehler */
    }
  }, [])

  const requestNewQuote = useCallback(() => {
    quotePromiseRef.current = null
    setQuote(null)
    setRequestNonce((n) => n + 1)
  }, [])

  const expiresMs = quote ? new Date(quote.expires_at).getTime() - now : 0
  const isExpired = Boolean(quote) && expiresMs <= 0
  const paymentUrl = quote ? buildHeliumPaymentUrl(quote) : ''

  return (
    <div data-testid="hnt-payment-pane">
      {error && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</p>
      )}

      {!quote && !error && (
        <p className="muted" style={{ textAlign: 'center', padding: '24px 0' }} data-testid="hnt-loading">
          {t('hnt.loading')}
        </p>
      )}

      {quote && (
        <div className="grid grid-2" style={{ alignItems: 'start' }}>
          <div>
            <div style={{ background: '#fff', padding: 12, borderRadius: 8 }}>
              <QRCodeSVG value={paymentUrl} size={280} level="M" className="qr-slot" data-testid="hnt-qr" />
            </div>
            <div className="row" style={{ justifyContent: 'center', marginTop: 12 }}>
              {isExpired ? (
                <button type="button" className="btn btn-sm" onClick={requestNewQuote} data-testid="hnt-new-quote-btn">
                  {t('hnt.newQuote')}
                </button>
              ) : (
                <a className="btn btn-sm" href={paymentUrl} target="_blank" rel="noopener noreferrer" data-testid="hnt-wallet-link">
                  {t('hnt.openWallet')}
                </a>
              )}
            </div>
            {!isExpired && (
              <p className="muted" style={{ fontSize: 11, textAlign: 'center', margin: '10px 0 0' }}>
                <Trans
                  t={t}
                  i18nKey="hnt.frozenFor"
                  values={{ time: formatCountdown(expiresMs) }}
                  components={{ b: <b className="mono" data-testid="hnt-countdown" /> }}
                />
              </p>
            )}
          </div>

          <div>
            <div className="copyline">
              <span><span className="muted">{t('hnt.amountLabel')}</span> <span className="mono" style={{ fontWeight: 600 }} data-testid="hnt-amount">{t('hnt.amountLine', { amount: quote.amount.toLocaleString(activeBcp47(), { maximumFractionDigits: 6 }), usd: formatMoney(quote.amount_usd, 'usd') })}</span></span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => { void copy(String(quote.amount), 'amount') }} data-testid="hnt-copy-amount">
                {copied === 'amount' ? t('hnt.copied') : t('hnt.copy')}
              </button>
            </div>
            <div className="copyline">
              <span className="mono ellipsis" title={quote.wallet_address} data-testid="hnt-address">
                {quote.wallet_address.slice(0, 6)}…{quote.wallet_address.slice(-6)}
              </span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => { void copy(quote.wallet_address, 'address') }} data-testid="hnt-copy-address">
                {copied === 'address' ? t('hnt.copied') : t('hnt.copy')}
              </button>
            </div>
            <div className="co-steps">
              {[1, 2, 3].map((n) => (
                <div className="co-step" key={n}>
                  <span className="co-step-n">{n}</span>
                  <div>
                    <b>{t(`hnt.steps.${n}.title`)}</b>
                    <span>{t(`hnt.steps.${n}.sub`)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {quote && (
        <>
          <div className="divider" />
          <div className={`statusline ${done ? 'is-done' : 'is-wait'}`} data-testid="hnt-status">
            <span className="sic">{done ? <Check size={12} /> : <Loader2 size={12} />}</span>
            <div>
              <b style={{ fontSize: 13 }}>{done ? t('hnt.status.done.title') : t('hnt.status.waiting.title')}</b>
              <div className="muted" style={{ fontSize: 11 }}>{done ? t('hnt.status.done.sub') : t('hnt.status.waiting.sub')}</div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
